import clsx from 'clsx'
import { useFormik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

interface IProps {
  closeModal: () => void
  selectedBot: any
}

const CreateBotSchema = Yup.object().shape({
  botName: Yup.string()
    .min(2, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Name is required'),
  botDescription: Yup.string()
    .min(2, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .required('Description is required'),
  startDate: Yup.string().nullable(),
  endDate: Yup.string().nullable()
})

const CreateInstagramBotModal: React.FC<IProps> = ({ closeModal, selectedBot }) => {
  const initialValues = {
    botName: '',
    botDescription: '',
    startDate: '',
    endDate: ''
  }

  let navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: CreateBotSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const token = localStorage.getItem('auth-token')
        const payload = {
          templateId: selectedBot?.[0]?.id,
          botName: values.botName,
          botDescription: values.botDescription,
          startDate: values.startDate,
          endDate: values.endDate
        }
        await axios.post(`${process.env.REACT_APP_RESUME_PARSER_URL}/instagram/create`, payload, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        resetForm()
        closeModal()
        navigate('/instagram-bots/all-bots')
      } catch (error) {
        console.error('Failed to create bot:', error)
      } finally {
        setSubmitting(false)
      }
    }
  })

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit}>
      <div className='fv-row mb-5'>
        <label className='form-label required'>Bot Name</label>
        <input
          {...formik.getFieldProps('botName')}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched.botName && formik.errors.botName,
            'is-valid': formik.touched.botName && !formik.errors.botName
          })}
          type='text'
          autoComplete='off'
        />
        {formik.touched.botName && formik.errors.botName && (
          <div className='text-danger'>{formik.errors.botName}</div>
        )}
      </div>

      <div className='fv-row mb-5'>
        <label className='form-label required'>Bot Description</label>
        <textarea
          {...formik.getFieldProps('botDescription')}
          className={clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched.botDescription && formik.errors.botDescription,
            'is-valid': formik.touched.botDescription && !formik.errors.botDescription
          })}
          style={{ height: '100px', resize: 'none' }}
        />
        {formik.touched.botDescription && formik.errors.botDescription && (
          <div className='text-danger'>{formik.errors.botDescription}</div>
        )}
      </div>

      <div className='fv-row mb-5'>
        <label className='form-label'>Start Date</label>
        <input
          {...formik.getFieldProps('startDate')}
          className='form-control form-control-lg form-control-solid'
          type='date'
        />
      </div>

      <div className='fv-row mb-10'>
        <label className='form-label'>End Date</label>
        <input
          {...formik.getFieldProps('endDate')}
          className='form-control form-control-lg form-control-solid'
          type='date'
        />
      </div>

      <div className='d-flex justify-content-end'>
        <button
          type='submit'
          className='btn btn-sm btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          <span className='indicator-label'>
            {formik.isSubmitting ? 'Creating...' : 'Create Bot'}
          </span>
        </button>
      </div>
    </form>
  )
}

export default CreateInstagramBotModal
